<template>
  <button
    :aria-label="disabled ? '' : $t('quote.select-timeslot', { timeslot: timeslotStart })"
    :disabled="disabled"
    type="button"
    class="px-4 py-2 lg:py-3 flex items-center text-sm border rounded-lg border-ocean-700 bg-white hover:bg-neutral-100 hover:text-neutral-800 focus:bg-neutral-100"
    :class="{
      'justify-center': hidePricing,
      'justify-between': !hidePricing,
    }"
    @click="$emit('select-timeslot')"
  >
    <span class="inline-flex space-x-1">
      <span class="text-ocean-900 font-semibold">
        {{ displayDate }}
      </span>
      <span class="text-ocean-900">
        {{ $t('common.at') }}
        {{ timeslotStart }}
      </span>
    </span>
    <span v-if="!hidePricing">
      <span class="text-neutral-800 font-semibold">
        <vetster-discount>
          <span>{{ amountFormatted }}</span>
        </vetster-discount>
      </span>
    </span>
  </button>
</template>

<script>
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapGetters } from 'vuex';

import VetsterDiscount from '@/components/VetsterDiscount';
import useAppointmentDatetimeFormat from '@/composables/use-appointment-datetime-format';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import { formatMoney } from '@/helpers/format';
import HasConversionRate from '@/mixins/HasConversionRate';

export default {
  name: 'VetsterSearchDateButton',

  components: { VetsterDiscount },

  mixins: [HasConversionRate],

  props: {
    appointment: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    timezone: {
      type: String,
      required: true,
    },
  },

  setup() {
    const root = getCurrentInstance().proxy.$root;
    const {
      getAppointmentDate,
      getAppointmentTime,
    } = useAppointmentDatetimeFormat();
    const { isSubscription } = useCheckoutHelpers(root);

    return {
      getAppointmentDate,
      getAppointmentTime,
      isSubscription,
    };
  },

  computed: {
    ...mapGetters('search', ['hidePriceForSearchPet']),
    amount() {
      return this.convertCurrency(this.appointment.rate.amount);
    },
    amountFormatted() {
      return this.$n(formatMoney(this.amount), {
        style: 'currency',
        currency: this.getConversionCurrency || this.appointment.rate.currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    },
    displayDate() {
      if (this.isToday) {
        return this.$t('search.today');
      }

      if (this.isTomorrow) {
        return this.$t('search.tomorrow');
      }

      return this.getAppointmentDate(this.appointment.start_at);
    },
    isToday() {
      const today = DateTime.local().startOf('day');
      const appointmentDate = DateTime.fromISO(this.appointment.start_at);

      return DateTime.isDateTime(appointmentDate)
        && appointmentDate.get('day') === today.get('day')
        && appointmentDate.get('month') === today.get('month')
        && appointmentDate.get('year') === today.get('year');
    },
    isTomorrow() {
      const today = DateTime.local().startOf('day');
      const appointmentDate = DateTime.fromISO(this.appointment.start_at);

      return DateTime.isDateTime(appointmentDate)
        && appointmentDate.get('day') === today.plus({ days: 1 }).get('day')
        && appointmentDate.get('month') === today.plus({ days: 1 }).get('month')
        && appointmentDate.get('year') === today.plus({ days: 1 }).get('year');
    },
    timeslotStart() {
      return this.getAppointmentTime(this.appointment.start_at, this.timezone);
    },
    hidePricing() {
      return this.hidePriceForSearchPet || this.isSubscription;
    },
  },
};
</script>
